// components/PincodePromptModal.jsx
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  DialogBackdrop,
} from "@headlessui/react";
import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import SummaryApi from "../common";
import { authHeader } from "../helpers/auth-header";
import { toast } from "sonner";
import { refreshPage } from "../helpers/refreshPage";
import { HiMapPin, HiDevicePhoneMobile, HiCheckCircle, HiPencil, HiXMark, HiBuildingOffice2 } from "react-icons/hi2";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { useSwipeVertical } from "../hooks/useSwipe";
import useAuth from "../hooks/useAuth";

/** Returns true if the raw pincode record has B2B delivery enabled.
 *  Checks several common field-name variants the backend may use.
 *  If no flag is present at all, returns true so we don't accidentally
 *  hide everything while the backend hasn't shipped the field yet. */
const isPincodeB2BEnabled = (item) => {
  const flag =
    item?.b2bDelivery ??
    item?.b2b_delivery ??
    item?.isB2BEnabled ??
    item?.is_b2b_enabled ??
    item?.b2bEnabled ??
    item?.b2b_enabled ??
    item?.allowB2B ??
    item?.allow_b2b ??
    null;
  // If the API hasn't added the field yet, show the pincode (safe fallback).
  if (flag === null || flag === undefined) return true;
  return flag === true || flag === 1 || flag === "1" || flag === "true";
};

const PincodePromptModal = ({ isOpen, setIsOpen, currentUser }) => {
  const { isBusiness } = useAuth();
  const [pincodeOptions, setPincodeOptions] = useState([]);
  const [selectedPincode, setSelectedPincode] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [mobileError, setMobileError] = useState("");
  const [isEditingMobile, setIsEditingMobile] = useState(false);

  // for optional profile fetch fallback
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [profileError, setProfileError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const existingMobile = currentUser?.mobileNumber || "";
  const existingPincode = currentUser?.pincode || "";

  const clearPincodeRelatedStorage = () => {
    // Clear flash sale timer
    localStorage.removeItem('flashSaleEndTime');
    localStorage.removeItem('flashSaleCooldownEndTime');
    
    // Clear recently viewed products
    localStorage.removeItem('recentlyViewed');
    
    // Clear wishlist and trigger storage event for context update
    localStorage.removeItem('wishlist');
    // Dispatch custom event to notify WishlistContext in the same window
    window.dispatchEvent(new Event('wishlistStorageChanged'));
  };

  const fetchPincodeOptions = async () => {
    try {
      const { data } = await axios.get(SummaryApi.pincode.url, {
        headers: authHeader(),
      });
      const rawList = data?.data || [];

      // For business users, only show pincodes where B2B delivery is enabled.
      const filtered = isBusiness
        ? rawList.filter(isPincodeB2BEnabled)
        : rawList;

      const mapped = filtered.map((item) => ({
        label: `${item.pincode} - ${item.regionName}`,
        value: item.pincode,
        raw: item,
      }));
      setPincodeOptions(mapped);
    } catch {
      toast.error("Failed to fetch pincode options.");
    }
  };

  const fetchProfile = async () => {
    // Only needed if currentUser doesn't provide what we need
    setLoadingProfile(true);
    setProfileError(null);
    try {
      const { data } = await axios.get(SummaryApi.profile.url, {
        headers: authHeader(),
      });

      const userInfo = data?.userInfo || {};
      const apiMobile = userInfo.mobileNumber || "";
      const apiPincode = userInfo.pincode || "";

      if (!existingMobile && apiMobile) setMobileNumber(apiMobile);
      if (!existingPincode && apiPincode) setSelectedPincode(apiPincode);
    } catch {
      setProfileError("Failed to load profile.");
    } finally {
      setLoadingProfile(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    fetchPincodeOptions();

    // Initialize from currentUser
    setMobileNumber(existingMobile || "");
    setSelectedPincode(existingPincode || "");

    // Read-only by default if we already have a stored mobile
    setIsEditingMobile(false);
    setMobileError("");

    // If currentUser didn't have data, try API profile
    if (!existingMobile || !existingPincode) {
      fetchProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const validateMobileNumber = (number) => {
    const plain = number.replace(/^\+?91/, ""); // strip +91 or 91
    return /^\d{10}$/.test(plain);
  };

  const getMobileError = (number) => {
    if (!number) return "";
    const isValid = validateMobileNumber(number);
    return isValid ? "" : "Enter a valid 10-digit mobile number.";
  };

  const sanitizeMobileInput = (raw) => {
    // allow starting + once, then digits; trim spaces
    let v = raw.replace(/\s+/g, "");
    if (v.startsWith("+")) {
      v = "+" + v.slice(1).replace(/\D/g, "");
    } else {
      v = v.replace(/\D/g, "");
    }
    // Cap length to a reasonable max (e.g., +911234567890 => 13)
    if (v.startsWith("+")) v = v.slice(0, 13);
    else v = v.slice(0, 12);
    return v;
  };

  const handleMobileChange = (e) => {
    const input = sanitizeMobileInput(e.target.value);
    setMobileNumber(input);
    const error = getMobileError(input);
    setMobileError(error);
  };

  const canSubmit = useMemo(() => {
    const hasSelectedPin = !!selectedPincode;

    // If there is an existing mobile and user is NOT editing it, mobile is considered valid/locked
    if (existingMobile && !isEditingMobile) {
      return hasSelectedPin && !mobileError;
    }

    // Otherwise validate entered/edited mobile
    if (!mobileNumber) return false;
    const isValid = validateMobileNumber(mobileNumber);
    return hasSelectedPin && isValid && !mobileError;
  }, [selectedPincode, mobileNumber, mobileError, isEditingMobile, existingMobile]);

  const handleSave = async () => {
    // Guard validation when needed
    if ((!existingMobile || isEditingMobile)) {
      const isValid = validateMobileNumber(mobileNumber);
      if (!isValid) {
        setMobileError("Enter a valid 10-digit mobile number.");
        return;
      }
    }

    setIsSaving(true);
    try {
      const payload = {
        pincode: selectedPincode,
      };

      // Only include mobileNumber if user is editing or there was none before
      if (!existingMobile || isEditingMobile) {
        payload.mobileNumber = mobileNumber;
      }

      // Check if pincode has changed
      const pincodeChanged = existingPincode && existingPincode !== selectedPincode;
      
      await axios.put(SummaryApi.profileUpdate.url, payload, {
        headers: authHeader(),
      });

      // If pincode changed, clear related localStorage items
      if (pincodeChanged) {
        clearPincodeRelatedStorage();
        toast.success("Delivery details updated successfully! Location-based data has been cleared.");
      } else {
        toast.success("Delivery details updated successfully!");
      }
      
      localStorage.removeItem("justLoggedIn");
      setTimeout(() => {
        refreshPage();
      }, 500);
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Failed to update details. Please try again.";
      toast.error(errorMessage);
    } finally {
      setIsSaving(false);
      setIsOpen(false);
    }
  };

  // Swipe down to close modal (only if not saving)
  const { ref: modalRef } = useSwipeVertical({
    onSwipeDown: () => {
      if (!isSaving) {
        setIsOpen(false);
      }
    },
    threshold: 80,
    enabled: isOpen && !isSaving,
  });

  return (
    <Dialog open={isOpen} onClose={() => {}} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-black/50 backdrop-blur-md transition-opacity" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel 
          ref={modalRef}
          className="w-full max-w-md rounded-2xl bg-white shadow-2xl transform transition-all border border-gray-100 animate-fadeIn touch-pan-y"
        >
          {/* Header Section */}
          <div className="relative bg-gradient-to-br from-orange-50 to-yellow-50 rounded-t-2xl p-6 border-b border-orange-100">
            <div className="flex items-center justify-center mb-4">
              <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-yellow-500 rounded-xl flex items-center justify-center shadow-lg">
                <HiMapPin className="text-2xl text-white" />
              </div>
            </div>
            <DialogTitle className="text-2xl font-bold text-gray-900 text-center mb-2">
              Choose Your Delivery Pincode
            </DialogTitle>
            <p className="text-sm text-center text-gray-600">
              Select your pincode and confirm your mobile number for faster delivery.
            </p>
          </div>

          {/* Content Section */}
          <div className="p-6 space-y-5">

            {/* Mobile Number Section */}
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center gap-2">
                <HiDevicePhoneMobile className="text-lg text-orange-600" />
                Mobile Number
              </label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <input
                    type="tel"
                    value={mobileNumber}
                    onChange={handleMobileChange}
                    placeholder="Enter 10-digit mobile number"
                    readOnly={!!existingMobile && !isEditingMobile}
                    className={`w-full pl-4 pr-4 py-3 rounded-xl border-2 shadow-sm focus:outline-none focus:ring-2 transition-all text-gray-900 ${
                      mobileError
                        ? "border-red-400 focus:ring-red-300 focus:border-red-500"
                        : "border-gray-300 focus:ring-orange-300 focus:border-orange-500"
                    } ${existingMobile && !isEditingMobile ? "bg-gray-50 cursor-not-allowed" : "bg-white"}`}
                  />
                  {!mobileError && mobileNumber && validateMobileNumber(mobileNumber) && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <HiCheckCircle className="text-green-500 text-xl" />
                    </div>
                  )}
                </div>

                {existingMobile && (
                  <button
                    type="button"
                    onClick={() => {
                      if (isEditingMobile) {
                        setMobileNumber(existingMobile);
                        setMobileError("");
                        setIsEditingMobile(false);
                      } else {
                        setIsEditingMobile(true);
                      }
                    }}
                    className={`px-4 py-3 rounded-xl border-2 font-medium transition-all duration-200 flex items-center gap-2 ${
                      isEditingMobile
                        ? "border-red-300 text-red-600 hover:bg-red-50"
                        : "border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-orange-300"
                    }`}
                  >
                    {isEditingMobile ? (
                      <>
                        <HiXMark className="text-lg" />
                        Cancel
                      </>
                    ) : (
                      <>
                        <HiPencil className="text-lg" />
                        Edit
                      </>
                    )}
                  </button>
                )}
              </div>

              {mobileError && (
                <p className="text-xs text-red-600 mt-2 flex items-center gap-1">
                  <ExclamationTriangleIcon className="h-4 w-4" />
                  {mobileError}
                </p>
              )}

              {existingMobile && !isEditingMobile && !mobileError && (
                <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                  <HiCheckCircle className="h-4 w-4 text-green-500" />
                  Mobile number verified
                </p>
              )}
            </div>

            {/* Pincode Dropdown Section */}
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center gap-2">
                <HiMapPin className="text-lg text-orange-600" />
                Delivery Pincode
              </label>

              {/* B2B: explain that only B2B-enabled pincodes are shown */}
              {isBusiness && (
                <div className="flex items-start gap-2 mb-3 px-3 py-2 bg-indigo-50 border border-indigo-200 rounded-lg text-xs text-indigo-700">
                  <HiBuildingOffice2 className="text-base flex-shrink-0 mt-0.5" />
                  <span>
                    Only pincodes with <span className="font-semibold">Business (B2B) delivery</span> enabled are shown for your account.
                  </span>
                </div>
              )}
              <div className="relative">
                <select
                  value={selectedPincode}
                  onChange={(e) => setSelectedPincode(e.target.value)}
                  className="w-full pl-4 pr-10 py-3 rounded-xl border-2 border-gray-300 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-500 text-gray-900 font-medium appearance-none cursor-pointer transition-all hover:border-orange-400"
                >
                  <option disabled value="">
                    Select your delivery pincode
                  </option>
                  {pincodeOptions.map((pin) => (
                    <option disabled={pin.value === "473112"} key={pin.value} value={pin.value}>
                      {pin.label}
                    </option>
                  ))}
                </select>
                {/* Custom Dropdown Arrow */}
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
              {selectedPincode && (
                <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                  <HiCheckCircle className="h-4 w-4" />
                  Pincode selected
                </p>
              )}
            </div>

            {/* Loading/Error States */}
            {loadingProfile && (
              <div className="flex items-center justify-center gap-2 py-3 bg-blue-50 border border-blue-200 rounded-xl">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent"></div>
                <p className="text-sm text-blue-600">Loading your details…</p>
              </div>
            )}
            {profileError && (
              <div className="flex items-center gap-2 py-3 bg-red-50 border border-red-200 rounded-xl">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-500 flex-shrink-0" />
                <p className="text-sm text-red-600">{profileError}</p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <button
                disabled={!canSubmit || isSaving}
                onClick={handleSave}
                className={`px-6 py-3 rounded-xl text-base font-bold text-white transition-all duration-200 shadow-lg flex items-center justify-center gap-2 min-w-[120px] ${
                  canSubmit && !isSaving
                    ? "bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600 hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98]"
                    : "bg-gray-300 cursor-not-allowed opacity-60"
                }`}
              >
                {isSaving ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <HiCheckCircle className="text-xl" />
                    Save
                  </>
                )}
              </button>
            </div>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
};

export default PincodePromptModal;
